import React from "react";
import { Link } from "react-router-dom";
import { cn } from "../../common/u1/cn";
import { SidebarNavItem } from "./SidebarNavItem";
import { SidebarWatermark } from "./SidebarWatermark";
// The white wordmark — the dark one is illegible on the navy rail.
import { ReactComponent as Logo } from "../../../assets/logo_white.svg";
import { ReactComponent as EnterpriseLogo } from "../../../assets/enterpriseLogo.svg";
import { ReactComponent as CollapsedLogo } from "../../../assets/icons/collapsedLogo.svg";
import { LogoutIcon } from "./navIcons";

/* The dashboard rail. Presentation only — the items, the collapse state and the unread dots come from useDashboardNav, which transcribes... */
export const DashboardSidebar = ({
  links = [],
  collapsed = false,
  onToggleCollapse,
  onLogout = () => {},
  isEnterprise = false,
  onNavigate,
  className = "",
  showCollapseControl = true,
}) => (
  <aside
    className={cn(
      /* `relative` + `overflow-hidden` so the oversized watermark is clipped to
         the rail rather than bleeding onto the page. 78px collapsed is the
         reference's icon-rail width. */
      "relative flex h-screen shrink-0 flex-col overflow-hidden bg-[image:var(--sidebar-grad)] pb-4 text-[var(--sidebar-dim)]",
      collapsed ? "w-[78px] px-[14px]" : "w-[240px] px-3",
      className
    )}
  >
    {!collapsed && <SidebarWatermark />}

    {/* The mark stays put while the nav scrolls beneath it. */}
    <div
      className={cn(
        "sticky top-0 z-[2] flex shrink-0 items-center gap-2 pb-5 pt-6",
        collapsed ? "justify-center px-0" : "pl-2 pr-1"
      )}
    >
      <Link to="/" aria-label="TREVEX home" className="flex min-w-0 items-center">
        {collapsed ? (
          <CollapsedLogo />
        ) : isEnterprise ? (
          <EnterpriseLogo />
        ) : (
          <Logo className="h-7 w-auto" />
        )}
      </Link>

      {showCollapseControl && !collapsed && (
        <button
          type="button"
          onClick={onToggleCollapse}
          aria-label="Collapse sidebar"
          aria-expanded="true"
          className="ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white/10 text-white/70 transition-colors hover:bg-white/20 hover:text-white"
        >
          <Chevron />
        </button>
      )}
    </div>

    <nav
      aria-label="Dashboard"
      className="u1-no-scrollbar relative z-[1] flex flex-1 flex-col gap-1 overflow-y-auto"
    >
      {links.map((item, i) => (
        <React.Fragment key={item.link}>
          {/* The design groups the rail: the primary destinations, then the
              trade products, then the account sections. */}
          {/* Transcribed from SidebarLinkGroup: the rail is grouped before
              these four items. */}
          {!collapsed &&
            ["Trade Information", "Following", "Knowledge Hub", "Help & Support"].includes(
              item.name
            ) && <hr className="my-3 border-t border-white/[0.08]" />}
          <SidebarNavItem
            name={item.name}
            link={item.link}
            Icon={item.Icon}
            alert={item.alert}
            accent={item.accent}
            collapsed={collapsed}
            onNavigate={onNavigate}
          />
        </React.Fragment>
      ))}

      <hr className="my-3 border-t border-white/[0.08]" />

      {/* An action, not a destination, so a button rather than a link. */}
      <button
        type="button"
        onClick={onLogout}
        title={collapsed ? "Logout" : undefined}
        aria-label={collapsed ? "Logout" : undefined}
        className={cn(
          // Same .nav-item geometry as the rows above it.
          "flex h-[46px] w-full shrink-0 items-center rounded-xl text-left text-[14.5px] font-medium text-[var(--sidebar-dim)] transition-colors hover:bg-white/[0.06] hover:text-white",
          collapsed ? "justify-center px-0" : "gap-3.5 px-4"
        )}
      >
        <LogoutIcon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
        {!collapsed && <span>Logout</span>}
      </button>
    </nav>

    {/* Collapsed, the handle sits at the foot of the rail: there is no logo row
        left to hang it from, and the bottom corner is out of the way of the
        icons. */}
    {showCollapseControl && collapsed && (
      <button
        type="button"
        onClick={onToggleCollapse}
        aria-label="Expand sidebar"
        aria-expanded="false"
        className="mx-auto mt-3 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/10 text-white/70 transition-colors hover:bg-white/20 hover:text-white"
      >
        <Chevron className="rotate-180" />
      </button>
    )}
  </aside>
);

const Chevron = ({ className = "" }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={className}
  >
    <path d="M15 6l-6 6 6 6" />
  </svg>
);

export default DashboardSidebar;
